import { useState, useEffect, useRef } from 'react';
import {
  collection,
  getDoc,
  doc,
  addDoc,
  updateDoc,
  query,
  where,
  serverTimestamp,
  onSnapshot,
  increment,
} from 'firebase/firestore';
import { db, httpsCallable, functions } from '../config/firebase';
import { getAuth } from 'firebase/auth';
import { MessageSquare, Send, Search, User } from 'lucide-react';

const Messages = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const messagesEndRef = useRef(null);
  const messagesUnsubscribeRef = useRef(null);
  const conversationsUnsubscribeRef = useRef(null);

  const auth = getAuth();
  const currentUser = auth.currentUser;

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Fetch conversations
  useEffect(() => {
    if (!currentUser) return;

    const fetchConversations = async () => {
      try {
        setLoading(true);
        console.log('Fetching conversations for user:', currentUser.uid);
        const conversationsRef = collection(db, 'conversations');

        // Remove orderBy to avoid index requirement, we'll sort client-side
        const conversationsQuery = query(
          conversationsRef,
          where('participants', 'array-contains', currentUser.uid),
        );

        // Set up real-time listener
        conversationsUnsubscribeRef.current = onSnapshot(
          conversationsQuery,
          async snapshot => {
            console.log(
              'Conversations snapshot received:',
              snapshot.docs.length,
              'docs',
            );
            const conversationsList = [];

            for (const convDoc of snapshot.docs) {
              const data = convDoc.data();
              const participants = data.participants || [];
              const otherParticipantId = participants.find(
                id => id !== currentUser.uid,
              );

              if (otherParticipantId) {
                try {
                  const userDoc = await getDoc(
                    doc(db, 'users', otherParticipantId),
                  );
                  const userData = userDoc.exists() ? userDoc.data() : null;

                  // Convert lastMessageTime to milliseconds for sorting
                  let lastMessageTimeMs = 0;
                  if (data.lastMessageTime) {
                    if (data.lastMessageTime.toDate) {
                      lastMessageTimeMs = data.lastMessageTime
                        .toDate()
                        .getTime();
                    } else if (data.lastMessageTime.seconds) {
                      lastMessageTimeMs = data.lastMessageTime.seconds * 1000;
                    } else if (data.lastMessageTime instanceof Date) {
                      lastMessageTimeMs = data.lastMessageTime.getTime();
                    } else if (typeof data.lastMessageTime === 'number') {
                      lastMessageTimeMs = data.lastMessageTime;
                    }
                  }

                  // Determine participant name - if stored name is "Admin", use user data instead
                  let participantName = 'Unknown User';
                  if (userData) {
                    participantName =
                      userData.displayName ||
                      `${userData.firstName || ''} ${
                        userData.lastName || ''
                      }`.trim() ||
                      userData.firstName ||
                      userData.email ||
                      'Unknown User';
                  } else if (
                    data.participantName &&
                    data.participantName !== 'Admin'
                  ) {
                    participantName = data.participantName;
                  }

                  conversationsList.push({
                    id: convDoc.id,
                    ...data,
                    participantId: otherParticipantId,
                    participantName, // Always use the determined name (not "Admin")
                    participantEmail: userData?.email || '',
                    participantAvatar: userData?.photoURL || null,
                    unreadCount: data.unreadCount?.[currentUser.uid] || 0,
                    lastMessageTimeMs, // For sorting
                  });
                } catch (error) {
                  console.error('Error fetching user data:', error);
                  conversationsList.push({
                    id: convDoc.id,
                    ...data,
                    participantId: otherParticipantId,
                    participantName: 'Unknown User',
                    unreadCount: data.unreadCount?.[currentUser.uid] || 0,
                    lastMessageTimeMs: 0,
                  });
                }
              }
            }

            // Sort by lastMessageTime descending (newest first)
            conversationsList.sort((a, b) => {
              const timeA = a.lastMessageTimeMs || 0;
              const timeB = b.lastMessageTimeMs || 0;
              return timeB - timeA;
            });

            console.log('Processed conversations:', conversationsList.length);
            setConversations(conversationsList);
            setLoading(false);
          },
          error => {
            console.error('Error in conversations snapshot:', error);
            console.error('Error code:', error.code);
            console.error('Error message:', error.message);
            setLoading(false);
          },
        );
      } catch (error) {
        console.error('Error setting up conversations listener:', error);
        console.error('Error details:', error.message, error.code);
        setLoading(false);
      }
    };

    fetchConversations();

    // Cleanup
    return () => {
      if (conversationsUnsubscribeRef.current) {
        conversationsUnsubscribeRef.current();
      }
      if (messagesUnsubscribeRef.current) {
        messagesUnsubscribeRef.current();
      }
    };
  }, [currentUser]);

  // Fetch messages for selected conversation
  useEffect(() => {
    if (!selectedConversation || !currentUser) {
      setMessages([]);
      return;
    }

    console.log(
      'Setting up messages listener for conversation:',
      selectedConversation.id,
    );

    // Cleanup previous subscription
    if (messagesUnsubscribeRef.current) {
      messagesUnsubscribeRef.current();
    }

    const messagesRef = collection(db, 'messages');
    // Remove orderBy to avoid index requirement, we'll sort client-side
    const messagesQuery = query(
      messagesRef,
      where('conversationId', '==', selectedConversation.id),
    );

    messagesUnsubscribeRef.current = onSnapshot(
      messagesQuery,
      snapshot => {
        console.log(
          'Messages snapshot received:',
          snapshot.docs.length,
          'messages',
        );
        const messagesList = snapshot.docs.map(doc => {
          const data = doc.data();
          let createdAt = new Date();

          // Convert Firestore timestamp to Date
          if (data.createdAt) {
            if (data.createdAt.toDate) {
              createdAt = data.createdAt.toDate();
            } else if (data.createdAt.seconds) {
              createdAt = new Date(data.createdAt.seconds * 1000);
            } else if (data.createdAt instanceof Date) {
              createdAt = data.createdAt;
            }
          }

          return {
            id: doc.id,
            ...data,
            createdAt,
          };
        });

        // Sort by createdAt ascending (oldest first)
        messagesList.sort((a, b) => {
          const timeA = a.createdAt?.getTime?.() || 0;
          const timeB = b.createdAt?.getTime?.() || 0;
          return timeA - timeB;
        });

        setMessages(messagesList);

        // Mark conversation as read
        if (selectedConversation.id) {
          updateDoc(doc(db, 'conversations', selectedConversation.id), {
            [`unreadCount.${currentUser.uid}`]: 0,
          }).catch(error => {
            console.error('Error marking conversation as read:', error);
          });
        }
      },
      error => {
        console.error('Error fetching messages:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
      },
    );

    return () => {
      if (messagesUnsubscribeRef.current) {
        messagesUnsubscribeRef.current();
      }
    };
  }, [selectedConversation, currentUser]);

  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedConversation || sendingMessage) return;

    setSendingMessage(true);
    try {
      // Get admin user document
      const adminUserDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const adminUserData = adminUserDoc.exists() ? adminUserDoc.data() : null;
      const adminDisplayName = 'Admin';

      const conversationId = selectedConversation.id;
      const targetUserId = selectedConversation.participantId;

      // Add message
      const messagesRef = collection(db, 'messages');
      await addDoc(messagesRef, {
        conversationId,
        text: messageText.trim(),
        senderId: currentUser.uid,
        senderName: adminDisplayName,
        senderDisplayName: 'Admin',
        senderEmail: currentUser.email || 'admin@flexyswap.com',
        isAdmin: true,
        role: 'admin',
        adminMessage: true,
        createdAt: serverTimestamp(),
        isRead: false,
      });

      // Get target user info for conversation name
      let targetUserName = 'User';
      try {
        const targetUserDoc = await getDoc(doc(db, 'users', targetUserId));
        if (targetUserDoc.exists()) {
          const targetUserData = targetUserDoc.data();
          targetUserName =
            targetUserData.displayName ||
            `${targetUserData.firstName || ''} ${
              targetUserData.lastName || ''
            }`.trim() ||
            targetUserData.firstName ||
            targetUserData.email ||
            'User';
        }
      } catch (error) {
        console.warn('Error fetching target user name:', error);
      }

      // Update conversation - don't set participantName to "Admin", keep it as the other user's name
      await updateDoc(doc(db, 'conversations', conversationId), {
        lastMessage: messageText.trim(),
        lastMessageTime: serverTimestamp(),
        [`unreadCount.${currentUser.uid}`]: 0,
        [`unreadCount.${targetUserId}`]: increment(1),
        // Don't override participantName - it should show the other user's name
        adminParticipant: true,
      });

      // Send notification
      try {
        const sendNotification = httpsCallable(functions, 'sendNotification');
        await sendNotification({
          userId: targetUserId,
          title: 'New Message from Admin',
          body: messageText.trim().substring(0, 100),
          data: { type: 'admin_message', conversationId },
        });
      } catch (notifError) {
        console.warn('Failed to send notification:', notifError);
      }

      setMessageText('');
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message: ' + error.message);
    } finally {
      setSendingMessage(false);
    }
  };

  const formatTime = date => {
    if (!date) return '';
    const d = new Date(date);
    const now = new Date();
    const diff = now - d;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
  };

  const formatMessageTime = date => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredConversations = conversations.filter(conv => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      conv.participantName?.toLowerCase().includes(search) ||
      conv.participantEmail?.toLowerCase().includes(search) ||
      conv.lastMessage?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col bg-white rounded-lg shadow-lg overflow-hidden">
      <div className="flex flex-1 overflow-hidden">
        {/* Conversations List */}
        <div className="w-1/3 border-r border-gray-200 flex flex-col bg-gray-50">
          <div className="p-4 border-b border-gray-200 bg-black">
            <h2 className="text-xl font-bold text-yellow-400 mb-3">Messages</h2>
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-800 text-yellow-300 border border-yellow-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-gray-500">Loading conversations...</div>
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-gray-500 text-center">
                  <MessageSquare
                    size={48}
                    className="mx-auto mb-2 text-gray-400"
                  />
                  <p>No conversations found</p>
                </div>
              </div>
            ) : (
              filteredConversations.map(conversation => {
                const isSelected = selectedConversation?.id === conversation.id;
                const hasUnread = conversation.unreadCount > 0;

                return (
                  <div
                    key={conversation.id}
                    onClick={() => setSelectedConversation(conversation)}
                    className={`p-4 border-b border-gray-200 cursor-pointer transition-colors ${
                      isSelected
                        ? 'bg-yellow-500 text-black'
                        : 'bg-white hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        {conversation.participantAvatar ? (
                          <img
                            src={conversation.participantAvatar}
                            alt={conversation.participantName}
                            className="w-12 h-12 rounded-full object-cover"
                            onError={e => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div
                          className={`w-12 h-12 rounded-full bg-yellow-500 flex items-center justify-center ${
                            conversation.participantAvatar ? 'hidden' : 'flex'
                          }`}
                        >
                          <span className="text-black font-semibold text-lg">
                            {conversation.participantName
                              ?.charAt(0)
                              ?.toUpperCase() || 'U'}
                          </span>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h3
                            className={`font-semibold truncate ${
                              isSelected ? 'text-black' : 'text-gray-900'
                            }`}
                          >
                            {conversation.participantName}
                          </h3>
                          {conversation.lastMessageTime && (
                            <span
                              className={`text-xs ${
                                isSelected ? 'text-gray-700' : 'text-gray-500'
                              }`}
                            >
                              {formatTime(
                                conversation.lastMessageTime?.toDate?.() ||
                                  conversation.lastMessageTime,
                              )}
                            </span>
                          )}
                        </div>
                        <p
                          className={`text-sm truncate ${
                            isSelected ? 'text-gray-700' : 'text-gray-600'
                          }`}
                        >
                          {conversation.lastMessage || 'No messages yet'}
                        </p>
                        {hasUnread && !isSelected && (
                          <div className="mt-1">
                            <span className="inline-block bg-yellow-500 text-black text-xs font-semibold px-2 py-0.5 rounded-full">
                              {conversation.unreadCount}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 flex flex-col bg-white">
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-gray-200 bg-black">
                <div className="flex items-center space-x-3">
                  {selectedConversation.participantAvatar ? (
                    <img
                      src={selectedConversation.participantAvatar}
                      alt={selectedConversation.participantName}
                      className="w-10 h-10 rounded-full object-cover"
                      onError={e => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div
                    className={`w-10 h-10 rounded-full bg-yellow-500 flex items-center justify-center ${
                      selectedConversation.participantAvatar ? 'hidden' : 'flex'
                    }`}
                  >
                    <span className="text-black font-semibold">
                      {selectedConversation.participantName
                        ?.charAt(0)
                        ?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-yellow-400">
                      {selectedConversation.participantName}
                    </h3>
                    {selectedConversation.participantEmail && (
                      <p className="text-sm text-yellow-300">
                        {selectedConversation.participantEmail}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Messages List */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                {messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-gray-500 text-center">
                      <MessageSquare
                        size={48}
                        className="mx-auto mb-2 text-gray-400"
                      />
                      <p>No messages yet. Start the conversation!</p>
                    </div>
                  </div>
                ) : (
                  messages.map(message => {
                    const isAdmin = message.senderId === currentUser?.uid;
                    return (
                      <div
                        key={message.id}
                        className={`flex ${
                          isAdmin ? 'justify-end' : 'justify-start'
                        }`}
                      >
                        <div
                          className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                            isAdmin
                              ? 'bg-yellow-500 text-black'
                              : 'bg-gray-200 text-gray-900'
                          }`}
                        >
                          {!isAdmin && (
                            <div className="text-xs font-semibold mb-1 text-gray-700">
                              {message.senderName ||
                                message.senderDisplayName ||
                                'User'}
                            </div>
                          )}
                          <p className="text-sm whitespace-pre-wrap break-words">
                            {message.text}
                          </p>
                          <p
                            className={`text-xs mt-1 ${
                              isAdmin ? 'text-gray-700' : 'text-gray-500'
                            }`}
                          >
                            {formatMessageTime(message.createdAt)}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div className="p-4 border-t border-gray-200 bg-black">
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={messageText}
                    onChange={e => setMessageText(e.target.value)}
                    onKeyPress={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    placeholder="Type a message..."
                    className="flex-1 px-4 py-2 bg-gray-800 text-yellow-300 border border-yellow-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    disabled={sendingMessage}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!messageText.trim() || sendingMessage}
                    className="bg-yellow-500 text-black px-6 py-2 rounded-lg font-semibold hover:bg-yellow-400 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {sendingMessage ? (
                      <>
                        <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        <span>Send</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-gray-50">
              <div className="text-center">
                <MessageSquare
                  size={64}
                  className="mx-auto mb-4 text-gray-400"
                />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  Select a conversation
                </h3>
                <p className="text-gray-500">
                  Choose a conversation from the list to start messaging
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Messages;
